import { useEffect, useState, useCallback } from "react";
import ContainerPage from "../../components/Containers/ContainerPage";
import TitlePage from "../../components/Texts/TitlePage";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import Loader from "../../components/Loaders/Loader";
import {
  BUTTON_NEW_CAMPAIGN,
  BUTTON_CREATE_FROM_SCRATCH,
  BUTTON_IMPORT_FROM_PLATFORM,
  LOADING_INFORMATION,
  FILTER_BY_DATES_PLACEHOLDER,
  ADS_CAMPAIGNS_DESCRIPTION,
  ADS_CAMPAIGNS_TITLE_DASHBOARD,
  DASHBOARD_METRICS_DELAY_WARNING,
  DASHBOARD_METRICS_DELAY_DISMISS,
} from "../../i18n/keysTranslations";
import {
  ADS_CAMPAIGN_ENTITIES,
  ADS_CAMPAIGN_STATUS_ACTIVE,
  ADS_CAMPAIGN_STATUS_DEACTIVATED,
  BORDER_RADIUS_BUTTON,
  FONT_SIZE_BUTTON,
  FONT_WEIGHT_BUTTON,
} from "../../utils/constants";
import { useStartCreateCampaign } from "../../hooks/useStartCreateCampaign";
import useAgencyHasImportCampaignsFeature from "../../hooks/useAgencyHasImportCampaignsFeature";
import {
  startUpdateCampaignEntityStatus,
  startUpdateUserHideMetricsAlert,
} from "../../actions/adsCampaigns";
import Box from "@mui/material/Box";
import _ from "lodash";
import { Typography, useMediaQuery, Alert, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AdsCampaignsTable from "./components/AdsCampaignsTable";
import { getNameBusinessTheme } from "../../actions/getters";
import { getStatusAdsCampaign } from "../../services/adsCampaigns";
import { useInView } from "react-intersection-observer";
import ButtonMenu from "../../components/Buttons/ButtonMenu";
import ModalImportAdsCampaign from "../../components/Modal/ModalImportAdsCampaign/ModalImportAdsCampaign";
import { Helmet } from "react-helmet";
import AddIcon from "@mui/icons-material/Add";
import { useForm } from "react-hook-form";
import RangeDateField from "../../components/Form/RangeDateField";
import useStatisticsWithDateRange from "../../hooks/useStatisticsWithDateRange";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import Button from "../../components/Buttons/Button";
import useAdsCampaignsList from "../../hooks/useAdsCampaignsList";

const AdsCampaigns = () => {
  const [modalImportAdsCampaignOpen, setModalImportAdsCampaignOpen] =
    useState(false);
  const [metricsAlertClosed, setMetricsAlertClosed] = useState(false);
  const [updatingStatusCampaignIDs, setUpdatingStatusCampaignIDs] = useState(
    []
  );
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const hasImportCampaignsFeature = useAgencyHasImportCampaignsFeature();
  const { startCreateCampaign, canCreateCampaign } = useStartCreateCampaign({
    fetchBrandOnMount: true,
  });
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());
  const { theme } = useSelectorApp((state) => state.ui);
  const hideDelayMetricsAlert = useSelectorApp(
    (state) => state.user.hideDelayMetricsAlert
  );

  // Custom hook for campaigns list and sorting
  const {
    campaigns,
    onChangeCampaigns,
    isLoadingConfiguration,
    isLoadingCampaigns,
    sortConfig,
    onTableSort,
    loadedLastCampaign,
    onGetNextCampaigns,
    syncingCampaignIDs,
    datePresetKey,
    onChangeDatePresetKey,
    savedDateRange,
  } = useAdsCampaignsList();

  const { control, watch, setValue } = useForm({
    defaultValues: {
      dateRange: {
        startDateTime: null,
        endDateTime: null,
      },
      createdBy: null,
    },
  });

  const { createdBy, dateRange } = watch();

  useEffect(() => {
    if (isLoadingConfiguration || !savedDateRange) return;

    setValue("dateRange", savedDateRange);
  }, [isLoadingConfiguration, savedDateRange, setValue]);

  const onDatePresetChange = useCallback(
    (presetKey, dateEpochs = {}) => {
      onChangeDatePresetKey({
        presetKey,
        startDate: dateEpochs?.startDate ?? null,
        endDate: dateEpochs?.endDate ?? null,
      });
    },
    [onChangeDatePresetKey]
  );

  const { ref, inView } = useInView({
    triggerOnce: false,
    rootMargin: "50px 0px",
  });

  const {
    statistics,
    previousPeriodStatistics,
    statisticsLoading,
    hasActiveDateRange,
  } = useStatisticsWithDateRange(campaigns, dateRange);

  const [columnVisibility, setColumnVisibility] = useState({
    impressions: false,
    cpm: false,
    clicks: true,
    cpc: false,
    ctr: false,
    results: true,
    cpa: false,
    spend: false,
    roas: false,
    frequency: false,
  });

  const toggleColumn = (column) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  const onCloseDelayMetricsAlert = () => setMetricsAlertClosed(true);

  const onDismissMetricsAlert = async () => {
    await dispatch(startUpdateUserHideMetricsAlert(true));
  };

  const onRedirectToCreateCampaign = startCreateCampaign;

  const onChangeStatusCampaign = async (campaignID) => {
    setUpdatingStatusCampaignIDs((prev) => [...prev, campaignID]);

    const campaign = campaigns[campaignID];
    const statusCampaign = getStatusAdsCampaign({
      states: campaign.states,
      target: "general",
    });
    const newStatus =
      statusCampaign === ADS_CAMPAIGN_STATUS_ACTIVE
        ? ADS_CAMPAIGN_STATUS_DEACTIVATED
        : ADS_CAMPAIGN_STATUS_ACTIVE;

    const response = await dispatch(
      startUpdateCampaignEntityStatus({
        campaignID,
        entity: ADS_CAMPAIGN_ENTITIES.ALL_CAMPAIGNS,
        entityID: campaignID,
        status: newStatus,
      })
    );

    if (response.ok) {
      onChangeCampaigns((prev) => {
        const currentCampaign = prev[campaignID] || campaign;
        return {
          ...prev,
          [campaignID]: {
            ...currentCampaign,
            states: {
              ...currentCampaign.states,
              general: newStatus,
            },
          },
        };
      });
    }

    setUpdatingStatusCampaignIDs((prev) =>
      prev.filter((id) => id !== campaignID)
    );

    return response.ok;
  };

  // Handle infinite scroll
  useEffect(() => {
    if (!inView || loadedLastCampaign || !_.isEmpty(statisticsLoading)) return;

    onGetNextCampaigns();
    // eslint-disable-next-line
  }, [inView, loadedLastCampaign, statisticsLoading]);

  const onGetFilteredCampaigns = () => {
    let finalCampaigns = campaigns;

    if (_.isEmpty(createdBy)) {
      return finalCampaigns;
    }

    if (!_.isEmpty(createdBy)) {
      const filteredKeys = Object.keys(finalCampaigns).filter((campaignID) =>
        createdBy.includes(finalCampaigns[campaignID].createdBy)
      );
      const filteredObject = {};
      filteredKeys.forEach((key) => {
        filteredObject[key] = finalCampaigns[key];
      });
      finalCampaigns = filteredObject;
    }

    return finalCampaigns;
  };

  const getEnrichedCampaigns = () => {
    const filteredCampaigns = onGetFilteredCampaigns();

    if (!hasActiveDateRange || !filteredCampaigns) {
      return filteredCampaigns;
    }

    // Enrich campaigns with date range statistics or use the existing statistics
    const enrichedCampaigns = {};
    Object.keys(filteredCampaigns).forEach((campaignID) => {
      const hasStatistics = statistics[campaignID];
      const hasPreviousStatistics = previousPeriodStatistics[campaignID];
      let finalStatistics = filteredCampaigns[campaignID].statistics;
      let finalPreviousStatistics =
        filteredCampaigns[campaignID].previousPeriodStatistics || {};

      if (hasStatistics) {
        let statisticsFormatted = {};
        Object.keys(hasStatistics).forEach((platform) => {
          const platformStatistics = hasStatistics[platform];
          const campaignStatistics = platformStatistics?.campaign || {};
          statisticsFormatted[platform] = campaignStatistics;
        });
        finalStatistics = statisticsFormatted;
      }

      if (hasPreviousStatistics) {
        let previousStatisticsFormatted = {};
        Object.keys(hasPreviousStatistics).forEach((platform) => {
          const platformStatistics = hasPreviousStatistics[platform];
          const campaignStatistics = platformStatistics?.campaign || {};
          previousStatisticsFormatted[platform] = campaignStatistics;
        });
        finalPreviousStatistics = previousStatisticsFormatted;
      }

      enrichedCampaigns[campaignID] = {
        ...filteredCampaigns[campaignID],
        statistics: finalStatistics,
        previousPeriodStatistics: finalPreviousStatistics,
      };
    });

    return enrichedCampaigns;
  };

  const filteredCampaigns = getEnrichedCampaigns();
  const hasCampaigns = !_.isEmpty(campaigns);
  const hasSomeCampaignActive =
    hasCampaigns &&
    Object.keys(campaigns || {}).some(
      (campaignID) =>
        campaigns?.[campaignID]?.states?.general &&
        campaigns?.[campaignID]?.states?.general === ADS_CAMPAIGN_STATUS_ACTIVE
    );

  if (isLoadingConfiguration)
    return (
      <Loader fullWidth hasMessage={true} message={t(LOADING_INFORMATION)} />
    );

  return (
    <>
      <Helmet>
        <title>{`${theme.nameBusiness} - ${t(ADS_CAMPAIGNS_TITLE_DASHBOARD)}`}</title>
      </Helmet>

      {hasSomeCampaignActive &&
        !hideDelayMetricsAlert &&
        !metricsAlertClosed && (
          <Box
            sx={{
              position: "fixed",
              bottom: 24,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1300,
              width: "90%",
              maxWidth: 600,
            }}
          >
            <Alert
              severity="info"
              onClose={onCloseDelayMetricsAlert}
              sx={{
                backgroundColor: "#fff",
                color: "text.primary",
                borderRadius: 2,
                boxShadow: "0 2px 16px rgba(0,0,0,0.15)",
                "& .MuiAlert-icon": { color: "info.main" },
              }}
              action={
                <IconButton
                  size="small"
                  aria-label="close"
                  onClick={onCloseDelayMetricsAlert}
                  sx={{ color: "text.secondary" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Box>
                  {t(DASHBOARD_METRICS_DELAY_WARNING, { NAME_BUSINESS })}
                </Box>
                <Box
                  component="span"
                  onClick={onDismissMetricsAlert}
                  sx={{
                    textDecoration: "underline",
                    fontStyle: "italic",
                    cursor: "pointer",
                  }}
                >
                  {t(DASHBOARD_METRICS_DELAY_DISMISS)}
                </Box>
              </Box>
            </Alert>
          </Box>
        )}

      <ContainerPage hasMarginBottom={false} sx={{ height: "100%" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr auto",
            },
            gridTemplateRows: {
              xs: "auto auto auto",
              md: "auto auto",
            },
            gridTemplateAreas: {
              xs: `
                "title"
                "subtitle"
                "actions"
              `,
              md: `
                "title actions"
                "subtitle subtitle"
              `,
            },
            width: "100%",
            px: 3,
            mt: 4,
            gap: 1,
            alignItems: { xs: "flex-start", md: "center" },
          }}
        >
          <Box sx={{ gridArea: "title" }}>
            <TitlePage>{t(ADS_CAMPAIGNS_TITLE_DASHBOARD)}</TitlePage>
          </Box>

          <Box sx={{ gridArea: "subtitle", pb: { xs: 1, md: 0 } }}>
            <Typography variant="body1" sx={{ color: "#878383" }}>
              {t(ADS_CAMPAIGNS_DESCRIPTION)}
            </Typography>
          </Box>

          <Box
            sx={{
              gridArea: "actions",
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
              width: { xs: "100%", md: "auto" },
              justifyContent: { xs: "flex-start", md: "flex-end" },
            }}
          >
            <RangeDateField
              showStartNowButton={false}
              defaultPlaceholderText={FILTER_BY_DATES_PLACEHOLDER}
              sx={{
                minWidth: "max-content",
                width: { xs: "100%", sm: "auto" },
                p: 1,
                borderRadius: BORDER_RADIUS_BUTTON,
                border: `1px solid ${INPUT_COLOR_BORDER}`,
                whiteSpace: "nowrap",
                height: 48,
                pl: 2,
                mr: 1,
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
              }}
              sxText={{
                fontSize: FONT_SIZE_BUTTON,
                lineHeight: 1,
                fontWeight: FONT_WEIGHT_BUTTON,
              }}
              startIcon={
                <CalendarTodayIcon
                  sx={{ fontSize: 14, color: "text.secondary" }}
                />
              }
              control={control}
              name="dateRange"
              mode="text"
              onSave={() => {}}
              initialPresetKey={datePresetKey}
              onPresetChange={onDatePresetChange}
              showIconText={true}
            />
            {canCreateCampaign && (
              <>
                {hasImportCampaignsFeature ? (
                  <ButtonMenu
                    mode="button"
                    textButton={BUTTON_NEW_CAMPAIGN}
                    fullWidth={isMobile}
                    options={[
                      {
                        label: BUTTON_CREATE_FROM_SCRATCH,
                        onClick: onRedirectToCreateCampaign,
                      },
                      {
                        label: BUTTON_IMPORT_FROM_PLATFORM,
                        onClick: () => setModalImportAdsCampaignOpen(true),
                      },
                    ]}
                    edge="start"
                    endIcon={
                      <AddIcon
                        sx={{
                          color: `${theme.palette.primary.contrastText} !important`,
                          ml: 0.5,
                          width: 20,
                          height: 20,
                        }}
                      />
                    }
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.main,
                      },
                    }}
                  />
                ) : (
                  <Button
                    fullWidth={isMobile}
                    onClick={onRedirectToCreateCampaign}
                    color="primary"
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    {t(BUTTON_NEW_CAMPAIGN)}
                    <AddIcon
                      sx={{
                        color: `${theme.palette.primary.contrastText} !important`,
                        ml: 0.5,
                        width: 20,
                        height: 20,
                      }}
                    />
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>
        <AdsCampaignsTable
          data={filteredCampaigns}
          loading={isLoadingCampaigns}
          onChangeStatusCampaign={onChangeStatusCampaign}
          lastItemRef={ref}
          loadedLastCampaign={loadedLastCampaign}
          columnVisibility={columnVisibility}
          onToggleColumn={toggleColumn}
          onSetColumnVisibility={setColumnVisibility}
          statisticsLoading={statisticsLoading}
          control={control}
          sortConfig={sortConfig}
          onTableSort={(columnKey) => onTableSort(columnKey, columnVisibility)}
          hasActiveDateRange={hasActiveDateRange}
          dateRange={dateRange}
          syncingCampaignIDs={syncingCampaignIDs}
          updatingStatusCampaignIDs={updatingStatusCampaignIDs}
        />
      </ContainerPage>
      {modalImportAdsCampaignOpen && (
        <ModalImportAdsCampaign
          modalOpen={modalImportAdsCampaignOpen}
          onCloseModal={() => setModalImportAdsCampaignOpen(false)}
        />
      )}
    </>
  );
};

export default AdsCampaigns;
